import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Modal from '../../Modal/Modal';
import BottomDrawer from '../../Drawer/Drawer';
import DeliveryAddress from '../../Buy/DeliveryAddress';
import { DeliveryAddressWrapper } from '../../Buy/SelectAddress';
import AddDeliveryAddressModal from '../Address/DeliveryAddressModal';
import {
  deleteWizardKey,
  setWizardContent,
} from '../../../util/helpers/wizardHelpers';
import {
  IS_ADD_BANK_ADDRESS_OPEN,
  WITHDRAWAL_ADDRESS,
} from '../../../constants/sessionStorage';
import { formatAddressString } from '../../../util/helpers/addressHelper';
import { selectIsAddAddressesEnabled } from '../../../store/selectors/settingsSelectors';
import { useSessionStorageState } from '../../../util/hooks/useSessionStorageState';
import ModalBody from '../../Modal/ModalBody';
import Paragraph from '../../Paragraph/Paragraph';
import Note from '../../Notes/Note';
import ModalButtons from '../../Modal/ModalButtons';
import { useBrokerCode } from '../../../util/hooks/useBrokerCode';
import { pxToRem } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import themeColors from '../../../assets/styles/themeColors';

const AddressCard = styled.div`
  background-color: #f8f9fa;
  border-radius: ${variables.borderRadius.borderRadius};
  padding: ${pxToRem(16)};
  display: grid;
`;

const AddressLabel = styled(Paragraph)`
  text-transform: uppercase;
`;

const AddNewAddressButton = styled.button`
  width: 100%;
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px dashed ${themeColors.colorGrey3};
  font-family: inherit;
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  background-color: transparent;
  padding: ${pxToRem(10)};
  cursor: pointer;
`;

/**
 * Shared by the wire-withdrawal order wizard (renders itself as a full Modal)
 * and the add-wire-withdrawal-account wizard (renders as plain step content
 * inside that wizard's single shared Modal, via renderAsModal={false}).
 */
const ConfirmBankStatementAddress = ({
  isModalOpen,
  handleBack,
  handleNext,
  handleClose,
  bankName,
  address,
  addresses,
  isLoading,
  setWithdrawalAddress,
  renderAsModal,
  setModalTitle,
  defaultModalTitle,
  wizardAddressKey,
}) => {
  const { t } = useTranslation();
  const brokerCode = useBrokerCode();
  const isAddAddressesEnabled = useSelector(selectIsAddAddressesEnabled);
  const [isChangingAddress, setIsChangingAddress] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useSessionStorageState(
    IS_ADD_BANK_ADDRESS_OPEN,
    false,
  );
  const [pendingSubmittedAddress, setPendingSubmittedAddress] = useState(null);

  useEffect(() => {
    if (!renderAsModal && setModalTitle) {
      setModalTitle(
        t('standardWithdrawalWizard.wireWithdrawal.confirmAddressTitle'),
      );
      return () => {
        if (defaultModalTitle) {
          setModalTitle(defaultModalTitle);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingSubmittedAddress || !addresses?.length) {
      return;
    }

    const matchedAddress = addresses.find(
      (savedAddress) =>
        savedAddress.AddressLine === pendingSubmittedAddress.AddressLine &&
        (savedAddress.AddressLine2 || '') ===
          (pendingSubmittedAddress.AddressLine2 || '') &&
        savedAddress.City === pendingSubmittedAddress.City &&
        savedAddress.StateRegion === pendingSubmittedAddress.StateRegion &&
        savedAddress.PostalCode === pendingSubmittedAddress.PostalCode,
    );

    if (matchedAddress) {
      const selectedAddress = {
        value: matchedAddress.AddressUid,
        label: formatAddressString(matchedAddress),
      };
      setWizardContent(wizardAddressKey, selectedAddress);
      setWithdrawalAddress(selectedAddress);
      setPendingSubmittedAddress(null);
    }
  }, [
    pendingSubmittedAddress,
    addresses,
    setWithdrawalAddress,
    wizardAddressKey,
  ]);

  const handleConfirm = (confirmedAddress) => {
    setWizardContent(wizardAddressKey, confirmedAddress);
    setWithdrawalAddress(confirmedAddress);
    handleNext();
  };

  const selectRawAddress = (rawAddress) => {
    const newAddress = {
      value: rawAddress.AddressUid,
      label: formatAddressString(rawAddress),
    };
    setWizardContent(wizardAddressKey, newAddress);
    setWithdrawalAddress(newAddress);
  };

  const handleSelectAddress = (selectedAddress) => {
    selectRawAddress(selectedAddress);
    setIsChangingAddress(false);
  };

  if (isAddAddressOpen) {
    return (
      <AddDeliveryAddressModal
        isOpen={isAddAddressOpen}
        onClose={() => {
          deleteWizardKey(IS_ADD_BANK_ADDRESS_OPEN);
          setIsAddAddressOpen(false);
        }}
        title={t('standardWithdrawalWizard.wireWithdrawal.addYourAddressTitle')}
        bankName={bankName}
        onAddressAdded={(newAddress, submittedAddress) => {
          if (newAddress?.AddressUid) {
            selectRawAddress(newAddress);
          } else if (submittedAddress) {
            setPendingSubmittedAddress(submittedAddress);
          }
          setIsChangingAddress(false);
        }}
      />
    );
  }

  const content = (
    <>
      <ModalBody>
        <Paragraph fontWeight={500} fontSize={16} marginBottom={16}>
          {t('standardWithdrawalWizard.wireWithdrawal.confirmAddressQuestion', {
            bankName,
          })}
        </Paragraph>
        <AddressCard>
          <AddressLabel
            fontSize={14}
            fontWeight={500}
            color="#3D3D3D"
            marginBottom={4}
          >
            {t('standardWithdrawalWizard.wireWithdrawal.bankStatementAddress')}
          </AddressLabel>
          <Paragraph fontSize={14} fontWeight={500} color="#505575">
            {address?.label}
          </Paragraph>
        </AddressCard>
        <Note
          text={t(
            'standardWithdrawalWizard.wireWithdrawal.confirmAddressWarning',
          )}
          marginTop={16}
          marginBottom={24}
        />
        <ModalButtons
          marginTop
          secondaryButtonProps={{
            label: t('standardWithdrawalWizard.wireWithdrawal.noChangeAddress'),
            onClick: () => setIsChangingAddress(true),
            fontSize: '16px',
            textTransform: 'none',
          }}
          primaryButtonProps={{
            label: t('standardWithdrawalWizard.wireWithdrawal.yesContinue'),
            onClick: () => handleConfirm(address),
            disabled: !address?.value,
            fontSize: '16px',
            textTransform: 'none',
            marginTop: 12,
          }}
        />
      </ModalBody>
      <BottomDrawer
        isOpen={isChangingAddress}
        onClose={() => setIsChangingAddress(false)}
        title={t(
          'standardWithdrawalWizard.wireWithdrawal.selectBankStatementAddress',
        )}
      >
        <DeliveryAddressWrapper>
          {isAddAddressesEnabled && brokerCode !== 'HAA' && (
            <AddNewAddressButton
              onClick={() => {
                setWizardContent(IS_ADD_BANK_ADDRESS_OPEN, true);
                setIsAddAddressOpen(true);
              }}
            >
              {t('buyWizard.addNewAddress')}
            </AddNewAddressButton>
          )}
          {(brokerCode === 'HAA'
            ? addresses?.filter((savedAddress) => savedAddress.IsVerified)
            : addresses
          )?.map((savedAddress) => (
            <DeliveryAddress
              key={savedAddress.AddressUid}
              address={savedAddress}
              isSelected={address?.value === savedAddress.AddressUid}
              onClick={handleSelectAddress}
            />
          ))}
        </DeliveryAddressWrapper>
      </BottomDrawer>
    </>
  );

  if (!renderAsModal) {
    return content;
  }

  return (
    <Modal
      title={t('standardWithdrawalWizard.wireWithdrawal.confirmAddressTitle')}
      size="sm"
      isOpen={isModalOpen}
      isLoading={isLoading}
      close={handleClose}
      goBack={handleBack}
      closeIconVisible
    >
      {content}
    </Modal>
  );
};

ConfirmBankStatementAddress.propTypes = {
  isModalOpen: PropTypes.bool,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  handleClose: PropTypes.func,
  bankName: PropTypes.string,
  address: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      AddressUid: PropTypes.string,
      AddressLine: PropTypes.string,
      AddressLine2: PropTypes.string,
      City: PropTypes.string,
      StateRegion: PropTypes.string,
      PostalCode: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  setWithdrawalAddress: PropTypes.func,
  renderAsModal: PropTypes.bool,
  setModalTitle: PropTypes.func,
  defaultModalTitle: PropTypes.string,
  wizardAddressKey: PropTypes.string,
};

ConfirmBankStatementAddress.defaultProps = {
  renderAsModal: true,
  wizardAddressKey: WITHDRAWAL_ADDRESS,
};

export default ConfirmBankStatementAddress;
